import { getConversationTemplate } from "@samklang/content";
import type { Perspective } from "@samklang/schemas";
import type { Kartlaggning, KartlaggningAction } from "@/stores/kartlaggning-store";
import type { MetricPoint, SamladBild } from "@/features/kartlaggning/lib";
import {
  actionStatusLabel,
  buildSamladBild,
  buildUskPerspectiveSections,
  formatDate,
  metricPoints,
  neutrallyDescribeDifference,
  perspectiveLabel,
  perspectiveShortLabel,
} from "@/features/kartlaggning/lib";

export type KartlaggningPdfType = "full" | "sammanfattning" | "usk";

/** What content to include in the generated PDF (checked in the preview panel). */
export interface KartlaggningPdfSelection {
  readonly perspectives: readonly Perspective[];
  readonly includeAnswers: boolean;
  readonly includeRatings: boolean;
  readonly includeCompilation: boolean;
  readonly includeStrengthsNeeds: boolean;
  readonly includeActions: boolean;
  readonly includeFollowUps: boolean;
  readonly includeDevelopment: boolean;
  readonly includeProvenance: boolean;
}

export interface KartlaggningPdfMeta {
  readonly label: string;
  readonly value: string;
}

export interface KartlaggningPdfSection {
  readonly heading: string;
  readonly lines: readonly string[];
}

export interface KartlaggningPdfDocument {
  readonly title: string;
  readonly subtitle: string;
  readonly meta: readonly KartlaggningPdfMeta[];
  readonly sections: readonly KartlaggningPdfSection[];
  readonly generatedAt: string;
}

function latin1(value: string): string {
  return value
    .normalize("NFC")
    .replace(/•/g, "-")
    .replace(/[–—]/g, "-")
    .replace(/→/g, "->")
    .replace(/[^ -ÿ]/g, "?");
}

function pdfText(value: string): string {
  return latin1(value).replace(/([\\()])/g, "\\$1");
}

function pdfBytes(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length);
  for (let index = 0; index < value.length; index += 1) bytes[index] = value.charCodeAt(index) & 0xff;
  return bytes;
}

function wrappedLines(value: string, width = 88): readonly string[] {
  const lines: string[] = [];
  for (const paragraph of value.split(/\r?\n/)) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    let line = "";
    for (const word of words) {
      if (line.length > 0 && line.length + word.length + 1 > width) {
        lines.push(line);
        line = word;
      } else {
        line = line ? `${line} ${word}` : word;
      }
    }
    if (line) lines.push(line);
    lines.push("");
  }
  return lines;
}

function wrappedPdfLine(value: string, width = 84): readonly string[] {
  if (!value) return [""];
  const lines = wrappedLines(value, width);
  return lines.at(-1) === "" ? lines.slice(0, -1) : lines;
}

function pageStream(lines: readonly string[]): string {
  const commands = ["BT", "/F1 11 Tf", "50 790 Td", "14 TL"];
  for (const line of lines) commands.push(`(${pdfText(line)}) Tj`, "T*");
  commands.push("ET");
  return commands.join("\n");
}

/**
 * Renders a structured kartläggning document to real PDF bytes.
 * Uses the same low-level object/stream writer as apps/api contract-pdf, adapted
 * for sectioned content with headings and Swedish character (Latin-1) handling.
 */
export function generateKartlaggningPdf(document: KartlaggningPdfDocument): Uint8Array {
  const rendered: string[] = [document.title.toUpperCase(), `Typ: ${document.subtitle}`, ""];
  for (const meta of document.meta) rendered.push(`${meta.label}: ${meta.value}`);
  rendered.push("", "TILLHANDAHÅLLNA DATA", ...wrappedLines("Underlaget skapades automatiskt ur lokalt sparade kartläggningssvar och stödjer professionell bedömning. Det avgör inte särskilt stöd i sig.", 84), "");
  for (const section of document.sections) {
    rendered.push(section.heading.toUpperCase(), "");
    for (const line of section.lines) rendered.push(...wrappedPdfLine(line));
    rendered.push("");
  }
  rendered.push("", "Slut på underlaget.");

  const pageLines: string[][] = [];
  for (let index = 0; index < rendered.length; index += 50) pageLines.push(rendered.slice(index, index + 50));

  const objects: string[] = [];
  const addObject = (content: string): number => {
    objects.push(content);
    return objects.length;
  };
  const catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>");
  if (catalogId !== 1) throw new Error("PDF_CATALOG_FAILED");
  addObject("");
  const fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>");
  const pageIds: number[] = [];
  for (const lines of pageLines) {
    const stream = pageStream(lines);
    const streamId = addObject(`<< /Length ${pdfBytes(stream).byteLength} >>\nstream\n${stream}\nendstream`);
    const pageId = addObject(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${streamId} 0 R >>`);
    pageIds.push(pageId);
  }
  objects[1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

  let pdf = "%PDF-1.4\n%âãÏÓ\n";
  const offsets = [0];
  objects.forEach((content, index) => {
    offsets.push(pdfBytes(pdf).byteLength);
    pdf += `${index + 1} 0 obj\n${content}\nendobj\n`;
  });
  const xref = pdfBytes(pdf).byteLength;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets.slice(1)) pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return pdfBytes(pdf);
}

export interface BuildKartlaggningPdfInput {
  readonly kartlaggning: Kartlaggning;
  readonly history: readonly Kartlaggning[];
  readonly type: KartlaggningPdfType;
  readonly selection: KartlaggningPdfSelection;
}

function developmentLines(participantReference: string, history: readonly Kartlaggning[]): readonly string[] {
  const points = metricPoints(participantReference, history);
  const byKey = new Map<string, readonly MetricPoint[]>();
  for (const point of points) {
    const prior = byKey.get(point.metricKey) ?? [];
    byKey.set(point.metricKey, [...prior, point]);
  }
  const sortedByDate = (series: readonly MetricPoint[]) => series.slice().sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return [...byKey.entries()].map(([key, series]) => {
    const label = series[0]?.metricLabel ?? key;
    const sorted = sortedByDate(series);
    const lastDate = sorted[sorted.length - 1];
    const perPerspective = [...new Set(sorted.map((point) => point.perspective))].map((perspective) => {
      const values = sorted.filter((point) => point.perspective === perspective).map((point) => `${point.value}`);
      return `${perspectiveShortLabel(perspective)} ${values.join("→")}`;
    });
    return `${label}: ${perPerspective.join(", ")}${lastDate ? ` · ${formatDate(lastDate.date)}` : ""}`;
  });
}

function actionLines(actions: readonly KartlaggningAction[]): readonly string[] {
  if (actions.length === 0) return ["Inga insatser är registrerade ännu."];
  return actions.map((action) =>
    [
      `${action.description} (${actionStatusLabel(action.status).toLowerCase()})`,
      `Syfte: ${action.purpose || "—"}`,
      `Ansvarig: ${action.responsible || "—"} · Start: ${formatDate(action.startDate)} · Uppföljning: ${formatDate(action.followUpDate)} · Hurdan: ${action.followUpMethod || "—"}`,
      "",
    ].join("\n"),
  );
}

function strengthsNeedsLines(samladBild: SamladBild): readonly string[] {
  const lines: string[] = [];
  if (samladBild.strengths.length) {
    lines.push("Styrkor och fungerande situationer");
    for (const entry of samladBild.strengths) lines.push(...wrappedLines(`• [${entry.label}] ${entry.item.question}: ${entry.item.answer}`, 84));
    lines.push("");
  }
  if (samladBild.difficulties.length) {
    lines.push("Svårigheter");
    for (const entry of samladBild.difficulties) lines.push(...wrappedLines(`• [${entry.label}] ${entry.item.question}: ${entry.item.answer}`, 84));
    lines.push("");
  }
  if (samladBild.needs.length) {
    lines.push("Behov");
    for (const entry of samladBild.needs) lines.push(...wrappedLines(`• [${entry.label}] ${entry.item.question}: ${entry.item.answer}`, 84));
    lines.push("");
  }
  return lines;
}

/** Pure builder turning a kartläggning (plus history for development) into a structured PDF document. */
export function buildKartlaggningPdfDocument(input: BuildKartlaggningPdfInput): KartlaggningPdfDocument {
  const { kartlaggning, history, type, selection } = input;
  const template = getConversationTemplate(kartlaggning.templateId);
  const selected = new Set(selection.perspectives);
  const selectedRuns = kartlaggning.runs.filter((run) => selected.has(run.perspective));
  const samladBild = buildSamladBild(kartlaggning);

  const meta: KartlaggningPdfMeta[] = [
    { label: "Elev / deltagare", value: kartlaggning.participantReference || "—" },
    { label: "Typ av samtal / kartläggning", value: `${kartlaggning.title}${template ? ` (${template.id})` : ""}` },
    { label: "Datum för kartläggning", value: formatDate(kartlaggning.createdAt) },
    { label: "Deltagande perspektiv", value: selectedRuns.map((run) => perspectiveLabel(run.perspective, run.role)).join(", ") || "—" },
  ];
  for (const run of selectedRuns) {
    if (run.status !== "done") continue;
    meta.push({
      label: `Genomfört · ${perspectiveLabel(run.perspective, run.role)}`,
      value: `Frågor genomförda av ${run.conductedBy || "—"}, tillsammans med ${run.conductedWith || "—"}, registrerade av ${run.registeredBy || "—"} (${formatDate(run.finishedAt)})`,
    });
  }

  const sections: KartlaggningPdfSection[] = [];

  if (type === "full" || type === "sammanfattning") {
    for (const run of selectedRuns) {
      const summary = run.summary ?? [];
      const heading = perspectiveLabel(run.perspective, run.role);
      if (type === "full") {
        const lines: string[] = [];
        for (const item of summary) {
          const question = item.question;
          const answer = item.metricValue !== undefined ? `${item.answer.replace(/ av (\d+)/, "/$1")}` : item.answer;
          lines.push(question);
          lines.push(`Svar: ${answer}`);
          if (selection.includeProvenance && item.questionId) {
            lines.push(`(${perspectiveShortLabel(run.perspective, run.role)} · ${run.conductedWith || "—"} · ${run.registeredBy || "—"} · ${formatDate(run.finishedAt)})`);
          }
          lines.push("");
        }
        sections.push({ heading, lines: lines.length ? lines : ["Detta perspektiv har inga registrerade svar ännu."] });
      } else {
        const metricRows = summary.filter((item) => item.metricValue !== undefined);
        sections.push({
          heading,
          lines:
            metricRows.length
              ? metricRows.map((item) => `${item.metricLabel ?? item.question}: ${item.metricValue}/${summary.find((s) => s.metricValue !== undefined)?.answer.match(/(\d+)$/)?.[1] ?? "5"}`)
              : ["Inga skattningsfrågor besvarades för detta perspektiv."],
        });
      }
    }
  }

  if (type === "usk") {
    for (const section of buildUskPerspectiveSections(kartlaggning)) {
      if (!selected.has(section.heading as Perspective) && !selectedRuns.some((run) => perspectiveLabel(run.perspective, run.role) === section.heading)) continue;
      const lines: string[] = [section.source, ""];
      for (const block of section.blocks) {
        lines.push(block.heading);
        lines.push(...(block.items.length ? block.items.map((item) => `• ${item.question}: ${item.answer}`) : ["Fylls i vid professionell bedömning."]));
        lines.push("");
      }
      sections.push({ heading: section.heading, lines });
    }
  }

  if (selection.includeRatings && type !== "usk") {
    const lines: string[] = [];
    for (const run of selectedRuns) {
      const metrics = (run.summary ?? []).filter((item) => item.metricValue !== undefined);
      if (!metrics.length) continue;
      lines.push(`${perspectiveLabel(run.perspective, run.role)}`);
      for (const item of metrics) lines.push(`• ${item.metricLabel ?? item.question}: ${item.metricValue}/${item.answer.match(/av\s+(\d+)/)?.[1] ?? "5"}`);
    }
    if (lines.length) sections.push({ heading: "Skattningar", lines });
  }

  if (selection.includeCompilation) {
    const lines: string[] = [];
    if (samladBild.agreementRows.length) {
      lines.push("Gemensamma nämnare");
      for (const row of samladBild.agreementRows) lines.push(...wrappedLines(`• ${row.question}: ${row.cells[0]?.answer}`, 84));
      lines.push("");
    }
    if (samladBild.differenceRows.length) {
      lines.push("Skillnader mellan perspektiv");
      for (const row of samladBild.differenceRows) lines.push(...wrappedLines(`• ${row.question} — ${neutrallyDescribeDifference(row)}`, 84));
      lines.push("");
    }
    if (lines.length) sections.push({ heading: "Samlad bild · Sammanställning", lines });
  }

  if (selection.includeStrengthsNeeds && type !== "usk") {
    const lines = strengthsNeedsLines(samladBild);
    if (lines.length) sections.push({ heading: "Styrkor, svårigheter och behov", lines });
  }

  if (selection.includeActions) {
    sections.push({ heading: "Insatser och överenskommelser", lines: actionLines(kartlaggning.actions) });
  }

  if (selection.includeFollowUps && kartlaggning.followUps.length) {
    sections.push({
      heading: "Uppföljningar",
      lines: kartlaggning.followUps.map((followUp) =>
        `${perspectiveLabel(followUp.perspective, followUp.role)}: ${followUp.value}/5 · ${formatDate(followUp.at)}${followUp.comment ? ` — ${followUp.comment}` : ""}`,
      ),
    });
  }

  if (selection.includeDevelopment) {
    const lines = developmentLines(kartlaggning.participantReference, history);
    sections.push({ heading: "Utveckling över tid", lines: lines.length ? lines : ["Inga skattningsserier finns för denna elev ännu."] });
  }

  if (kartlaggning.reusedSessionNotes.length) {
    sections.push({ heading: "Återanvänt tidigare underlag", lines: kartlaggning.reusedSessionNotes });
  }

  if (type === "usk") {
    sections.push({
      heading: "Samlad bild",
      lines: [
        ...(samladBild.agreementRows.length ? ["Gemensamma nämnare:", ...samladBild.agreementRows.map((row) => `• ${row.question}: ${row.cells[0]?.answer}`), ""] : []),
        ...(samladBild.differenceRows.length ? ["Skillnader:", ...samladBild.differenceRows.map((row) => `• ${row.question} — ${neutrallyDescribeDifference(row)}`), ""] : []),
        ...strengthsNeedsLines(samladBild),
        "Lärmiljöfaktorer: Fylls i vid professionell bedömning.",
        "Anpassningar och effekt: Fylls i vid professionell bedömning.",
        "Utforska vidare: Fylls i vid professionell bedömning.",
      ],
    });
  }

  if (type === "usk") {
    sections.push({
      heading: "Viktigt att notera",
      lines: ["Detta underlag stödjer professionell bedömning och avgör inte särskilt stöd."],
    });
  }

  return {
    title: `${kartlaggning.title} · Kartläggningsunderlag`,
    subtitle: type === "full" ? "Fullständigt underlag" : type === "sammanfattning" ? "Sammanfattat underlag" : "USK-underlag",
    meta,
    sections,
    generatedAt: new Date().toISOString(),
  };
}
